// src/components/AuthScreens.js
import React, { useState } from 'react';
import { FUNCTION_URL, FUNCTION_KEY } from '../utils/constants';

function buildBearerHeaders(accessToken) {
  return {
    Authorization: `Bearer ${accessToken}`,
  };
}

function addFunctionKeyToUrl(url) {
  if (FUNCTION_KEY) {
    url.searchParams.set('code', FUNCTION_KEY);
  }
  return url;
}

export function AccessDenied({ userName, userRole, organization, getAccessTokenSilently, userEmail }) {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleViewAcceptedDsa = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const url = new URL(`${FUNCTION_URL}/api/v1/dsa/accepted-document`);
      url.searchParams.set('format', 'pdf');
      
      // Add email parameter if available
      if (userEmail) {
        url.searchParams.set('email', userEmail);
      }
      
      addFunctionKeyToUrl(url);

      const accessToken = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });

      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: buildBearerHeaders(accessToken),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to fetch PDF: ${res.status} - ${errorText.slice(0, 200)}`);
      }

      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      
      const safeName = userName.replace(/\s+/g, '_');
      const agency = organization || "unv";
      link.download = `${safeName}_${agency}_dsa.pdf`;
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error viewing accepted DSA:', error);
      alert('Unable to load accepted DSA document. It might still be processing or not yet available.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="content-container access-denied-wrapper">
      <p style={{
        textAlign: 'center',
        color: '#666', 
        marginBottom: '1rem',
        fontSize: '1.1em'
      }}>
        You have accepted the DSA
        {userName && userRole && (
          <span> as <strong>{userName}</strong> ({userRole})</span>
        )}
        {' - '}
        <button
          onClick={handleViewAcceptedDsa}
          disabled={isDownloading}
          style={{ 
            background: 'none',
            border: 'none',
            color: isDownloading ? '#999' : '#0066cc',
            textDecoration: 'underline',
            cursor: isDownloading ? 'not-allowed' : 'pointer',
            padding: 0,
            font: 'inherit'
          }}
        >
          {isDownloading ? 'Downloading...' : 'Download Accepted Agreement (.pdf)'}
        </button>
      </p>

      <div className="access-denied">
        <h2>Access Pending Approval</h2>
        <p className="highlight">
          Your account is pending approval by an administrator. Access will be granted shortly.
        </p>
      </div>
    </div>
  );
}

export function EmailNotVerified() {
  return (
    <div className="content-container access-denied-wrapper">
      <div className="access-denied">
        <h2>Email Verification Required</h2>
        <p className="highlight">
          Your email address has not been verified yet.
        </p>
        <p>
          Please check your inbox and click the confirmation link we sent you to complete the verification process.
        </p>
      </div>
    </div>
  );
}

export function UserInactive() {
  return (
    <div className="content-container access-denied-wrapper">
      <div className="access-denied">
        <h2>Account Inactive</h2>
        <p className="highlight">
          Your account has been deactivated.
        </p>
        <p>
          Please contact your administrator for more information or to request reactivation.
        </p>
      </div>
    </div>
  );
}

export function AccountExpired({ expirationDate }) {
  const formattedDate = expirationDate 
    ? new Date(expirationDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'N/A';

  return (
    <div className="content-container access-denied-wrapper">
      <div className="access-denied">
        <h2>Account Expired</h2>
        <p className="highlight">
          Your account access expired on {formattedDate}.
        </p>
        <p>
          Please contact your administrator to request an extension or renewal of your access.
        </p>
      </div>
    </div>
  );
}

export function UserInactiveAndExpired({ expirationDate }) {
  const formattedDate = expirationDate 
    ? new Date(expirationDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'N/A';

  return (
    <div className="content-container access-denied-wrapper">
      <div className="access-denied">
        <h2>Account Inactive and Expired</h2>
        <p className="highlight">
          Your account has been deactivated and your access expired on {formattedDate}.
        </p>
        <p>
          Please contact your administrator for assistance with reactivating and renewing your account.
        </p>
      </div>
    </div>
  );
}

export function DsaSuccess() {
  return (
    <div className="content-container access-denied-wrapper">
      <div
        style={{
          textAlign: 'center',
          maxWidth: '640px',
          margin: '0 auto',
          padding: '24px',
        }}
      >
        <h2 style={{ color: '#2f6f2f' }}>Success!</h2>
        <p
          style={{
            color: '#2f855a',
            backgroundColor: '#c6f6d5',
            padding: '1rem',
            borderRadius: '0.25rem',
            fontWeight: '600',
            display: 'inline-block',
            marginBottom: '1rem',
          }}
        >
          Your acceptance of the DSA has been registered and archived.
        </p>
        <p style={{ marginTop: '1rem', color: '#333' }}>
          Next Steps: you will receive an email to your signup email address.
          Kindly click the confirmation link to verify your account (if you
          have not already done so). UNV will review your request and you will
          be notified once full access is granted.
        </p>
        <p
          style={{
            marginTop: '1rem',
            fontSize: '0.9em',
            color: '#666',
          }}
        >
          You must log in again after completing these steps.
        </p>
      </div>
    </div>
  );
}

export function Loading() {
  return <div className="loading-container">Loading...</div>;
}

export function LoginPrompt({ onLogin, onSignUp }) {
  return (
    <div className="content-container auth-landing">
      <div className="auth-card">
        <h3 className="auth-title">Welcome</h3>
        <p className="auth-subtitle">
          Please sign up or log in to access the data download service.
        </p>
        <div className="auth-actions">
          <button onClick={onSignUp} className="btn btn-primary">Sign Up</button>
          <button onClick={onLogin} className="btn btn-secondary">Log In</button>
        </div>
      </div>
    </div>
  );
}